"use strict";
var __extends = (this && this.__extends) || function (d, b) {
    for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p];
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
};
var ndarray = require('ndarray');
var pool = require('typedarray-pool');
var compileMesher = require('greedy-mesher');
var compileCWise = require('cwise-compiler');
var createBuffer = require('gl-buffer');
var createVAO = require('gl-vao');
var Mesher_1 = require('@voxeline/mesher/lib/Mesher');
exports.OPAQUE_BIT = (1 << 15);
exports.VOXEL_MASK = (1 << 15) - 1;
exports.AO_SHIFT = 16;
exports.AO_BITS = 2;
exports.AO_MASK = (1 << exports.AO_BITS) - 1;
exports.FLIP_BIT = (1 << (exports.AO_SHIFT + 4 * exports.AO_BITS));
exports.AO_TABLE = new Uint8Array([0, 153, 204, 255]);
exports.VERTEX_SIZE = 12;
// Calculates ambient occlusion level for a vertex
function vertexAO(s1, s2, c) {
    if (s1 && s2) {
        return 1;
    }
    return 3 - (s1 + s2 + c);
}
// Calculates the ambient occlusion bit mask for a facet
function facetAO(a00, a01, a02, a10, a12, a20, a21, a22) {
    var s00 = (a00 & exports.OPAQUE_BIT) ? 1 : 0;
    var s01 = (a01 & exports.OPAQUE_BIT) ? 1 : 0;
    var s02 = (a02 & exports.OPAQUE_BIT) ? 1 : 0;
    var s10 = (a10 & exports.OPAQUE_BIT) ? 1 : 0;
    var s12 = (a12 & exports.OPAQUE_BIT) ? 1 : 0;
    var s20 = (a20 & exports.OPAQUE_BIT) ? 1 : 0;
    var s21 = (a21 & exports.OPAQUE_BIT) ? 1 : 0;
    var s22 = (a22 & exports.OPAQUE_BIT) ? 1 : 0;
    return (vertexAO(s10, s01, s00) << exports.AO_SHIFT) +
        (vertexAO(s01, s12, s02) << (exports.AO_SHIFT + exports.AO_BITS)) +
        (vertexAO(s12, s21, s22) << (exports.AO_SHIFT + 2 * exports.AO_BITS)) +
        (vertexAO(s21, s10, s20) << (exports.AO_SHIFT + 3 * exports.AO_BITS));
}
// Generates a surface voxel, complete with ambient occlusion type
function generateSurfaceVoxel(v000, v001, v002, v010, v011, v012, v020, v021, v022, v100, v101, v102, v110, v111, v112, v120, v121, v122) {
    var t0 = !(v011 & exports.OPAQUE_BIT);
    var t1 = !(v111 & exports.OPAQUE_BIT);
    if (v111 && (!v011 || (t0 && !t1))) {
        return v111 | exports.FLIP_BIT | facetAO(v000, v001, v002, v010, v012, v020, v021, v022);
    }
    else if (v011 && (!v111 || (t1 && !t0))) {
        return v011 | facetAO(v100, v101, v102, v110, v112, v120, v121, v122);
    }
}
// Compile surface stencil operator
function arg(name, lvalue, rvalue, count) {
    return { name: name, lvalue: lvalue, rvalue: rvalue, count: count };
}
var empty_proc = {
    args: [],
    thisVars: [],
    localVars: [],
    body: '',
};
var cwise_args = ['scalar', 'array', 'array', 'array', 'array'];
var cwise_arg_names = [
    arg('_func', false, true, 3),
    arg('_o0', true, false, 1),
    arg('_o1', true, false, 1),
    arg('_o2', true, false, 1),
];
var cwise_body = [];
for (var d = 0; d < 3; ++d) {
    var u = (d + 1) % 3;
    var v = (d + 2) % 3;
    var expr = [];
    for (var dz = 0; dz < 2; ++dz)
        for (var dy = 0; dy <= 2; ++dy)
            for (var dx = 0; dx <= 2; ++dx) {
                var x = [dx, dy, dz];
                expr.push(['_a', x[v], x[u], x[d]].join(''));
            }
    cwise_body.push(['_o', d, '=_func(', expr.join(','), ')'].join(''));
}
var cwise_body_str = cwise_body.join('\n');
for (var dx = -1; dx <= 1; ++dx)
    for (var dy = -1; dy <= 1; ++dy)
        for (var dz = -1; dz <= 1; ++dz) {
            if (dx === 1 && dy === 1 && dz === 1) {
                continue;
            }
            if (!(dx === -1 && dy === -1 && dz === -1)) {
                cwise_args.push({ offset: [dx + 1, dy + 1, dz + 1], array: 3 });
            }
            var carg_name = ['_a', dx + 1, dy + 1, dz + 1].join('');
            cwise_arg_names.push(arg(carg_name, false, true, cwise_body_str.split(carg_name).length - 1));
        }
var surfaceStencil = compileCWise({
    args: cwise_args,
    pre: empty_proc,
    body: { args: cwise_arg_names, body: cwise_body_str, thisVars: [], localVars: [] },
    post: empty_proc,
    funcName: 'calcAO'
}).bind(undefined, generateSurfaceVoxel);
var AoMesher = (function (_super) {
    __extends(AoMesher, _super);
    function AoMesher(openEnd) {
        _super.call(this, { openEnd: openEnd, margin: 1 });
        this.vertexData = new Uint8Array(4);
    }
    AoMesher.prototype.createVao = function (gl, padded) {
        var data = this.computeMeshData(padded);
        var buffer = createBuffer(gl, data);
        var vao = createVAO(gl, [{
                buffer: buffer,
                type: gl.UNSIGNED_BYTE,
                size: 4,
                offset: 0,
                stride: exports.VERTEX_SIZE,
                normalized: false,
            }, {
                buffer: buffer,
                type: gl.UNSIGNED_BYTE,
                size: 4,
                offset: 4,
                stride: exports.VERTEX_SIZE,
                normalized: false,
            }, {
                buffer: buffer,
                type: gl.UNSIGNED_BYTE,
                size: 4,
                offset: 8,
                stride: exports.VERTEX_SIZE,
                normalized: false,
            }]);
        vao.length = data.length / exports.VERTEX_SIZE;
        return vao;
    };
    AoMesher.prototype.computeMeshData = function (padded) {
        var unpadded = this.unpad(padded);
        var sz = unpadded.shape[0] * unpadded.shape[1] * unpadded.shape[2];
        var scratch0 = pool.mallocInt32(sz);
        var scratch1 = pool.mallocInt32(sz);
        var scratch2 = pool.mallocInt32(sz);
        var ao0 = ndarray(scratch0, unpadded.shape);
        var ao1 = ndarray(scratch1, unpadded.shape);
        var ao2 = ndarray(scratch2, unpadded.shape);
        // Calculate ao fields
        surfaceStencil(ao0, ao1, ao2, unpadded);
        this.buffer = pool.mallocUint8(1024);
        this.ptr = 0;
        this.computeSurface(0, ao0);
        this.computeSurface(1, ao1);
        this.computeSurface(2, ao2);
        // Release buffers
        pool.freeInt32(scratch0);
        pool.freeInt32(scratch1);
        pool.freeInt32(scratch2);
        var data = this.buffer.subarray(0, this.ptr);
        this.buffer = null;
        return data;
    };
    AoMesher.prototype.append = function (loX, loY, hiX, hiY, val) {
        // Grow buffer if we exceed capacity
        if (this.ptr + 6 * exports.VERTEX_SIZE > this.buffer.length) {
            var tmp = pool.mallocUint8(2 * this.buffer.length);
            tmp.set(this.buffer);
            pool.freeUint8(this.buffer);
            this.buffer = tmp;
        }
        var z = this.z | 0;
        var d = this.d | 0;
        this.flip = !!(val & exports.FLIP_BIT);
        var a00 = exports.AO_TABLE[((val >>> exports.AO_SHIFT) & exports.AO_MASK)];
        var a10 = exports.AO_TABLE[((val >>> (exports.AO_SHIFT + exports.AO_BITS)) & exports.AO_MASK)];
        var a11 = exports.AO_TABLE[((val >>> (exports.AO_SHIFT + 2 * exports.AO_BITS)) & exports.AO_MASK)];
        var a01 = exports.AO_TABLE[((val >>> (exports.AO_SHIFT + 3 * exports.AO_BITS)) & exports.AO_MASK)];
        // pack normal into 6 bits
        var sign = this.flip ? 127 : 129;
        var nx;
        var ny;
        var nz;
        if (d === 0) {
            nx = sign;
            ny = 128;
            nz = 128;
        }
        else if (d === 1) {
            nx = 128;
            ny = sign;
            nz = 128;
        }
        else if (d === 2) {
            nx = 128;
            ny = 128;
            nz = sign;
        }
        var flipAO = a00 + a11 === a10 + a01
            ? Math.max(a00, a11) < Math.max(a10, a01)
            : a00 + a11 < a10 + a01;
        this.getVertexData(this.vertexData, val & exports.VOXEL_MASK);
        if (flipAO) {
            if (!this.flip) {
                this.writeBuffer(loX, loY, z, a00, nx, ny, nz);
                this.writeBuffer(loX, hiY, z, a01, nx, ny, nz);
                this.writeBuffer(hiX, loY, z, a10, nx, ny, nz);
                this.writeBuffer(hiX, hiY, z, a11, nx, ny, nz);
                this.writeBuffer(hiX, loY, z, a10, nx, ny, nz);
                this.writeBuffer(loX, hiY, z, a01, nx, ny, nz);
            }
            else {
                this.writeBuffer(loX, loY, z, a00, nx, ny, nz);
                this.writeBuffer(hiX, loY, z, a10, nx, ny, nz);
                this.writeBuffer(loX, hiY, z, a01, nx, ny, nz);
                this.writeBuffer(hiX, hiY, z, a11, nx, ny, nz);
                this.writeBuffer(loX, hiY, z, a01, nx, ny, nz);
                this.writeBuffer(hiX, loY, z, a10, nx, ny, nz);
            }
        }
        else {
            if (this.flip) {
                this.writeBuffer(loX, hiY, z, a01, nx, ny, nz);
                this.writeBuffer(loX, loY, z, a00, nx, ny, nz);
                this.writeBuffer(hiX, hiY, z, a11, nx, ny, nz);
                this.writeBuffer(hiX, loY, z, a10, nx, ny, nz);
                this.writeBuffer(hiX, hiY, z, a11, nx, ny, nz);
                this.writeBuffer(loX, loY, z, a00, nx, ny, nz);
            }
            else {
                this.writeBuffer(loX, loY, z, a00, nx, ny, nz);
                this.writeBuffer(loX, hiY, z, a01, nx, ny, nz);
                this.writeBuffer(hiX, hiY, z, a11, nx, ny, nz);
                this.writeBuffer(hiX, hiY, z, a11, nx, ny, nz);
                this.writeBuffer(hiX, loY, z, a10, nx, ny, nz);
                this.writeBuffer(loX, loY, z, a00, nx, ny, nz);
            }
        }
    };
    AoMesher.prototype.writeBuffer = function (u, v, d, ao, nx, ny, nz) {
        // attrib0
        this.buffer[this.ptr + this.u] = u;
        this.buffer[this.ptr + this.v] = v;
        this.buffer[this.ptr + this.d] = d;
        this.buffer[this.ptr + 3] = ao;
        // attrib1
        this.buffer[this.ptr + 4] = nx;
        this.buffer[this.ptr + 5] = ny;
        this.buffer[this.ptr + 6] = nz;
        // this.buffer[this.ptr+7] = padding
        // attrib2
        this.buffer.set(this.vertexData, this.ptr + 8);
        this.ptr += exports.VERTEX_SIZE;
    };
    return AoMesher;
}(Mesher_1.default));
Object.defineProperty(exports, "__esModule", { value: true });
exports.default = AoMesher;
//# sourceMappingURL=AoMesher.js.map


/*****************
 ** WEBPACK FOOTER
 ** /Users/songseunggeun/voxeline/engine/lib/meshers/AoMesher.js
 ** module id = 199
 ** module chunks = 0
 **/